import { supabase } from './SupabaseConfig';
import { TableRepository, Table } from '../../domain/pos/ports/TableRepository';
import { InfrastructureError } from '@core/errors/AppError';

export class SupabaseTableRepository implements TableRepository {
  async getTables(userId: string, locationId: string): Promise<Table[]> {
    try {
      const { data, error } = await supabase
        .from('tables')
        .select('*')
        .eq('owner_id', userId)
        .eq('location_id', locationId)
        .order('table_number', { ascending: true });

      if (error) throw error;
      return (data || []).map(t => ({
        id: t.id,
        tableNumber: t.table_number,
        qrCodeUrl: t.qr_code_url,
        currentOrderId: t.current_order_id,
        status: t.status,
        locationId: t.location_id,
        updatedAt: t.updated_at
      }));
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy danh sách bàn (Supabase)', 'Supabase', error as Error);
    }
  }

  async getTable(userId: string, locationId: string, tableId: string): Promise<Table | null> {
    try {
      const { data, error } = await supabase
        .from('tables')
        .select('*')
        .eq('id', tableId)
        .eq('owner_id', userId)
        .eq('location_id', locationId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        tableNumber: data.table_number,
        qrCodeUrl: data.qr_code_url,
        currentOrderId: data.current_order_id,
        status: data.status,
        locationId: data.location_id,
        updatedAt: data.updated_at
      };
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy thông tin bàn (Supabase)', 'Supabase', error as Error);
    }
  }

  async createTable(userId: string, locationId: string, tableData: Partial<Table>): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('tables')
        .insert({
          owner_id: userId,
          location_id: locationId,
          table_number: tableData.tableNumber,
          qr_code_url: tableData.qrCodeUrl,
          status: 'empty',
          updated_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      throw new InfrastructureError('Lỗi tạo bàn mới (Supabase)', 'Supabase', error as Error);
    }
  }

  async updateTable(userId: string, locationId: string, tableId: string, updates: Partial<Table>): Promise<void> {
    try {
      const payload: any = { updated_at: new Date().toISOString() };
      if (updates.status) payload.status = updates.status;
      if (updates.tableNumber) payload.table_number = updates.tableNumber;
      if (updates.qrCodeUrl) payload.qr_code_url = updates.qrCodeUrl;
      if (updates.currentOrderId !== undefined) payload.current_order_id = updates.currentOrderId;

      const { error } = await supabase
        .from('tables')
        .update(payload)
        .eq('id', tableId)
        .eq('owner_id', userId)
        .eq('location_id', locationId);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi cập nhật bàn (Supabase)', 'Supabase', error as Error);
    }
  }

  async deleteTable(userId: string, locationId: string, tableId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('tables')
        .delete()
        .eq('id', tableId)
        .eq('owner_id', userId)
        .eq('location_id', locationId);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi xóa bàn (Supabase)', 'Supabase', error as Error);
    }
  }

  listenTables(userId: string, locationId: string, callback: (tables: Table[]) => void): () => void {
    const channel = supabase
      .channel(`tables-${locationId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tables',
        filter: `location_id=eq.${locationId}`
      }, async () => {
        const tables = await this.getTables(userId, locationId);
        callback(tables);
      })
      .subscribe();

    // Initial fetch
    this.getTables(userId, locationId).then(callback);

    return () => {
      supabase.removeChannel(channel);
    };
  }
}
